import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { LoadingSpinner } from '@/components/shared'
import { ProtectedRoute } from './guards/ProtectedRoute'
import { OnboardingGuard } from './guards/OnboardingGuard'
import { AppLayout } from '@/layouts/AppLayout'

// Lazy load all pages
const LandingPage = lazy(() => import('@/pages/LandingPage'))
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const SignupPage = lazy(() => import('@/pages/auth/SignupPage'))
const BetaGate = lazy(() => import('@/pages/BetaGate'))
const WaitlistSuccess = lazy(() => import('@/pages/WaitlistSuccess'))
const AboutPage = lazy(() => import('@/pages/AboutPage'))
const ContactPage = lazy(() => import('@/pages/ContactPage'))
const PrivacyPolicy = lazy(() => import('@/pages/legal/PrivacyPolicy'))
const TermsOfService = lazy(() => import('@/pages/legal/TermsOfService'))

const OnboardingLayout = lazy(() => import('@/pages/onboarding/OnboardingLayout'))
const OnboardingStart = lazy(() => import('@/pages/onboarding/OnboardingStart'))
const OnboardingLocations = lazy(() => import('@/pages/onboarding/OnboardingLocations'))
const OnboardingTone = lazy(() => import('@/pages/onboarding/OnboardingTone'))
const OnboardingReview = lazy(() => import('@/pages/onboarding/OnboardingReview'))

const DashboardPage = lazy(() => import('@/pages/app/DashboardPage'))
const ReviewDetailPage = lazy(() => import('@/pages/app/ReviewDetailPage'))
const AnalyticsPage = lazy(() => import('@/pages/app/AnalyticsPage'))
const SettingsPage = lazy(() => import('@/pages/app/settings/SettingsPage'))

function LoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <LoadingSpinner size="lg" label="Loading..." />
    </div>
  )
}

export function AppRoutes() {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/signup" element={<SignupPage />} />
          <Route path="/beta-gate" element={<BetaGate />} />
          <Route path="/waitlist/success" element={<WaitlistSuccess />} />

          {/* Onboarding routes (protected but not onboarding-guarded) */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/onboarding/start" replace />} />
            <Route path="start" element={<OnboardingStart />} />
            <Route path="locations" element={<OnboardingLocations />} />
            <Route path="tone" element={<OnboardingTone />} />
            <Route path="review" element={<OnboardingReview />} />
          </Route>

          {/* App routes (protected + onboarding-guarded) */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout />
                </OnboardingGuard>
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="reviews/:reviewId" element={<ReviewDetailPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
